import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bell, BellRing, Ban, Clock3, Loader2, RefreshCw, Mail, MessageSquare, Smartphone, Check, PhoneOff } from "lucide-react";
import { Card, Pill, Quiet, Note, Money } from "@/components/hub/Shell";

/**
 * Deal alerts — a case every desk has passed on, or one nobody has touched for days.
 * Same data in two places: the bell in the command bar, and a card on the overview.
 */
const KIND = {
  declined: { Icon: Ban, pill: "dead", dot: "bg-[#0B1220]", fallback: "Declined" },
  stalled: { Icon: Clock3, pill: "wait", dot: "bg-[#7da9ff]", fallback: "Gone quiet" },
  no_contact: { Icon: PhoneOff, pill: "act", dot: "bg-[#0055FF]", fallback: "Trying to reach the client" },
};

const ago = (iso) => {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins || 1}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};

export function useDealAlerts(interval = 30000) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/hub/deal-alerts", { params: { open_only: 1, limit: 50 } });
      setData(r.data);
    } catch { setData({ items: [], open: 0, total: 0 }); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, interval);
    window.addEventListener("deal-alerts:changed", load);
    return () => { clearInterval(id); window.removeEventListener("deal-alerts:changed", load); };
  }, [load, interval]);

  return { data, reload: load };
}

const Landed = ({ ok, Icon, title }) => (
  <span title={title} className={ok ? "text-teal-600" : "text-slate-300"}>
    <Icon className="size-3" />
  </span>
);

function Delivery({ d = {}, direct }) {
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="hub-alert-delivery">
      {!direct && (
        <>
          <span className="text-[10px] uppercase tracking-[0.12em] text-slate-400">intro</span>
          <Landed ok={d.introducer_email} Icon={Mail} title={d.introducer_email ? "Emailed the introducer" : "Introducer email did not send"} />
          <Landed ok={d.introducer_sms} Icon={MessageSquare} title={d.introducer_sms ? "Texted the introducer" : "No mobile on file, or the text did not send"} />
          <Landed ok={d.introducer_push} Icon={Smartphone} title={d.introducer_push ? "Pushed to their installed portal" : "No push subscription"} />
          <span className="text-slate-200">|</span>
        </>
      )}
      <span className="text-[10px] uppercase tracking-[0.12em] text-slate-400">desk</span>
      <Landed ok={d.admin_email} Icon={Mail} title={d.admin_email ? "Emailed the desk" : "Desk email did not send"} />
      <Landed ok={d.admin_sms} Icon={MessageSquare} title={d.admin_sms ? "Texted the desk" : "No desk mobile configured"} />
    </span>
  );
}

function AlertRow({ a, onAck, busy }) {
  const k = KIND[a.kind] || KIND.stalled;
  return (
    <div className="flex items-start gap-3 py-3" data-testid={`hub-alert-${a.id}`}>
      <span className={`mt-[7px] size-1.5 shrink-0 rounded-full ${k.dot}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/hub/leads/${a.lead_id}`}
                className="min-w-0 max-w-full sm:truncate text-[13px] font-semibold text-slate-900 transition-colors hover:text-[#0055FF]"
                data-testid={`hub-alert-case-${a.id}`}>
            {a.business || a.applicant || "A case"}
          </Link>
          <Pill tone={k.pill}>{a.kind_label || k.fallback}</Pill>
          {a.silent && <Pill tone="slate">logged only</Pill>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-slate-500">
          <Money value={a.amount_gbp} tone="muted" size="xs" />
          {a.finance_type && <span>· {a.finance_type}</span>}
          <span>· {a.referrer_name ? `via ${a.referrer_name}` : "Connexis Direct"}</span>
          {a.lender_name && <span>· {a.lender_name}</span>}
          {a.days_quiet ? <span className="font-semibold text-[#3B63A8]">· quiet {a.days_quiet}d</span> : null}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <Delivery d={a.delivered} direct={!a.referrer_id} />
          <span className="text-[11px] text-slate-400">{ago(a.created_at)}</span>
        </div>
      </div>
      <button onClick={() => onAck(a.id)} disabled={busy === a.id}
              className="mt-0.5 inline-flex h-7 shrink-0 items-center gap-1 rounded-[10px] border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 disabled:opacity-50"
              data-testid={`hub-alert-ack-${a.id}`}>
        {busy === a.id ? <Loader2 className="size-3 animate-spin" /> : <Check className="size-3" />} Clear
      </button>
    </div>
  );
}

function useAlertActions(reload) {
  const [busy, setBusy] = useState(null);
  const [sweeping, setSweeping] = useState(false);

  const ack = async (id) => {
    setBusy(id);
    try {
      await api.post(`/hub/deal-alerts/${id}/ack`);
      await reload();
      window.dispatchEvent(new Event("deal-alerts:changed"));
    } catch { toast.error("That alert could not be cleared"); }
    finally { setBusy(null); }
  };

  const sweep = async () => {
    setSweeping(true);
    try {
      const r = await api.post("/hub/deal-alerts/stalls/run");
      const n = r.data?.raised || 0;
      toast.success(n
        ? `${n} case${n === 1 ? "" : "s"} flagged — the introducer and the desk have been told`
        : `Nothing has been quiet for ${r.data?.threshold_days || 5} days`);
      await reload();
      window.dispatchEvent(new Event("deal-alerts:changed"));
    } catch { toast.error("The stall check could not run"); }
    finally { setSweeping(false); }
  };

  return { busy, ack, sweeping, sweep };
}

/** AlertBell — lives in the Hub command bar, on every page. */
export function AlertBell() {
  const { data, reload } = useDealAlerts();
  const { busy, ack, sweeping, sweep } = useAlertActions(reload);
  const items = data?.items || [];
  const open = data?.open || 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex h-7 items-center gap-1.5 rounded-[10px] border px-2.5 text-[11.5px] transition-colors ${
            open > 0
              ? "border-[#0055FF]/30 bg-[#0055FF]/[0.07] font-semibold text-[#0044CC] hover:border-[#0055FF]/50"
              : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
          data-testid="hub-topbar-alerts">
          {open > 0 ? <BellRing className="size-3" /> : <Bell className="size-3" />}
          {open > 0
            ? <><span className="hub-num" data-testid="hub-topbar-alerts-count">{open}</span> deal alert{open === 1 ? "" : "s"}</>
            : "no deal alerts"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[440px] max-w-[calc(100vw-2rem)] p-0" data-testid="hub-alerts-panel">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-slate-900">Deal alerts</div>
            <div className="text-[11px] text-slate-400">Passed on by every desk, or nobody has touched it for days.</div>
          </div>
          <Button onClick={sweep} disabled={sweeping} size="sm" variant="outline"
                  className="h-7 shrink-0 rounded-[10px] text-[11px]" data-testid="hub-alerts-run-sweep">
            {sweeping ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
            <span className="ml-1">Check now</span>
          </Button>
        </div>
        {items.length === 0 ? (
          <div className="px-4 py-6 text-[12.5px] leading-relaxed text-slate-400" data-testid="hub-alerts-empty">
            Nothing to look at. Every live case has moved in the last five days and no case has been
            turned down by its whole panel.
          </div>
        ) : (
          <div className="hub-scroll max-h-[440px] divide-y divide-slate-100 overflow-y-auto px-4" data-testid="hub-alerts-list">
            {items.map((a) => <AlertRow key={a.id} a={a} onAck={ack} busy={busy} />)}
          </div>
        )}
        <div className="border-t border-slate-100 px-4 py-2.5 text-[11px] text-slate-400">
          Alerts reach the introducer by email, text and push the moment they fire — mute settings
          do not apply to them.
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** DealAlertsCard — the same alerts, on the overview. */
export function DealAlertsCard() {
  const { data, reload } = useDealAlerts(45000);
  const { busy, ack, sweeping, sweep } = useAlertActions(reload);
  if (!data) return null;
  const items = data.items || [];

  return (
    <Card label="Deal alerts" Icon={BellRing} tone={items.length ? "act" : "plain"} testId="hub-deal-alerts"
          right={
            <div className="flex items-center gap-2">
              {items.length > 0 && (
                <Pill tone="act" testId="hub-deal-alerts-count">
                  {items.length} open{data.total > items.length ? ` · ${data.total} all time` : ""}
                </Pill>
              )}
              <Button onClick={sweep} disabled={sweeping} size="sm" variant="outline"
                      className="h-7 rounded-[10px] text-[11.5px]" data-testid="hub-deal-alerts-sweep">
                {sweeping ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="mr-1 size-3" />} Check now
              </Button>
            </div>
          }>
      {items.length === 0 ? (
        <>
          <Quiet testId="hub-deal-alerts-empty">
            Nothing turned down by a full panel, and nothing sitting untouched for five days.
          </Quiet>
          <Note>
            When a case is declined by every desk, or goes five days without moving, the introducer
            gets an email, a text and a push straight away — and it lands here until you clear it.
          </Note>
        </>
      ) : (
        <div className="mt-2 divide-y divide-slate-100" data-testid="hub-deal-alerts-list">
          {items.slice(0, 8).map((a) => <AlertRow key={a.id} a={a} onAck={ack} busy={busy} />)}
          {items.length > 8 && (
            <div className="pt-2.5 text-[11.5px] text-slate-400">+ {items.length - 8} more in the bell, top right</div>
          )}
        </div>
      )}
    </Card>
  );
}
